import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CircleCheck, MailCheck } from 'lucide-react';
import { forgotPasswordSchema, signInSchema, signUpSchema, ROLE_LABELS, type SessionDto, type WorkspaceRole } from '@onesource/shared';
import { api, ApiError, errorMessage } from '@/lib/api';
import { sessionKey, useSession } from '@/hooks/session';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/form-controls';
import { Callout } from '@/components/common';
import { Skeleton } from '@/components/ui/display';

export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <svg viewBox="0 0 32 32" className="size-7" aria-hidden>
            <rect width="32" height="32" rx="8" className="fill-accent" />
            <circle cx="16" cy="16" r="7" fill="none" stroke="white" strokeWidth="3" />
            <circle cx="16" cy="16" r="2.5" fill="white" />
          </svg>
          <span className="text-base font-semibold tracking-tight">Knownlead</span>
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-muted">{subtitle}</p>}
        <div className="mt-6">{children}</div>
        {footer && <div className="mt-6 text-[13px] text-muted">{footer}</div>}
      </div>
    </div>
  );
}

function applyFieldErrors(err: unknown, setError: (name: never, e: { message: string }) => void) {
  if (err instanceof ApiError) for (const [k, v] of Object.entries(err.fields)) setError(k as never, { message: v });
}

function useAfterAuth() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (session: SessionDto) => {
    qc.setQueryData(sessionKey, session);
    const next = params.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) navigate(next, { replace: true });
    else navigate(session.workspaces[0] ? `/w/${session.workspaces[0].id}/inbox` : '/onboarding', { replace: true });
  };
}

export function SignInPage() {
  const after = useAfterAuth();
  const form = useForm<z.infer<typeof signInSchema>>({ resolver: zodResolver(signInSchema) });
  const mutation = useMutation({
    mutationFn: (values: z.infer<typeof signInSchema>) => api.post<SessionDto>('/api/auth/signin', values),
    onSuccess: after,
  });
  return (
    <AuthLayout title="Sign in" subtitle="Welcome back. Your inquiries are waiting.">
      <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        {mutation.isError && <Callout tone="danger">{errorMessage(mutation.error)}</Callout>}
        <Field label="Email" error={form.formState.errors.email?.message}>
          {(p) => <Input {...p} type="email" autoComplete="email" autoFocus {...form.register('email')} />}
        </Field>
        <Field label="Password" error={form.formState.errors.password?.message}>
          {(p) => <Input {...p} type="password" autoComplete="current-password" {...form.register('password')} />}
        </Field>
        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="text-[13px] text-muted hover:text-foreground">
            Forgot password?
          </Link>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Sign in
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

/** Private: reachable only via the secret link (/signup?key=…) or from an invitation. */
export function SignUpPage() {
  const after = useAfterAuth();
  const [params] = useSearchParams();
  const form = useForm<z.infer<typeof signUpSchema>>({ resolver: zodResolver(signUpSchema) });
  const next = params.get('next');
  const signupKey = params.get('key') ?? undefined;
  const inviteToken = next?.startsWith('/invite?') ? (new URLSearchParams(next.slice('/invite?'.length)).get('token') ?? undefined) : undefined;
  const mutation = useMutation({
    mutationFn: (values: z.infer<typeof signUpSchema>) => api.post<SessionDto>('/api/auth/signup', { ...values, signupKey, inviteToken }),
    onSuccess: after,
    onError: (err) => applyFieldErrors(err, form.setError as never),
  });
  if (!signupKey && !inviteToken) {
    return (
      <AuthLayout title="Page not found" subtitle="The page you’re looking for doesn’t exist or you don’t have access to it.">
        <Button asChild variant="primary">
          <Link to="/">Go to Knownlead</Link>
        </Button>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      title="Create your account"
      subtitle="One inbox for every customer inquiry."
      footer={
        <>
          Already have an account?{' '}
          <Link className="font-medium text-accent hover:underline" to={`/signin${next ? `?next=${encodeURIComponent(next)}` : ''}`}>
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        {mutation.isError && !(mutation.error instanceof ApiError && Object.keys(mutation.error.fields).length) && (
          <Callout tone="danger">{errorMessage(mutation.error)}</Callout>
        )}
        <Field label="Full name" error={form.formState.errors.name?.message}>
          {(p) => <Input {...p} autoComplete="name" autoFocus {...form.register('name')} />}
        </Field>
        <Field label="Work email" error={form.formState.errors.email?.message}>
          {(p) => <Input {...p} type="email" autoComplete="email" {...form.register('email')} />}
        </Field>
        <Field label="Password" hint="At least 10 characters." error={form.formState.errors.password?.message}>
          {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register('password')} />}
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending}>
          Create account
        </Button>
        <p className="text-center text-xs text-muted">
          By creating an account you agree to the{' '}
          <a href="/terms" className="underline hover:text-foreground" target="_blank" rel="noopener">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy" className="underline hover:text-foreground" target="_blank" rel="noopener">
            Privacy Policy
          </a>
          .
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const form = useForm<z.infer<typeof forgotPasswordSchema>>({ resolver: zodResolver(forgotPasswordSchema) });
  const mutation = useMutation({ mutationFn: (v: z.infer<typeof forgotPasswordSchema>) => api.post('/api/auth/forgot-password', v) });
  return (
    <AuthLayout title="Reset your password" subtitle="We’ll email you a link to choose a new one." footer={<Link className="text-accent hover:underline" to="/signin">Back to sign in</Link>}>
      {mutation.isSuccess ? (
        <Callout tone="success" icon={MailCheck} title="Check your inbox">
          If an account exists for {form.getValues('email')}, a reset link is on its way. It expires in one hour.
        </Callout>
      ) : (
        <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
          {mutation.isError && <Callout tone="danger">{errorMessage(mutation.error)}</Callout>}
          <Field label="Email" error={form.formState.errors.email?.message}>
            {(p) => <Input {...p} type="email" autoComplete="email" autoFocus {...form.register('email')} />}
          </Field>
          <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

const resetForm = z.object({ password: z.string().min(10, 'Use at least 10 characters').max(200), confirm: z.string() }).refine((v) => v.password === v.confirm, {
  path: ['confirm'],
  message: 'Passwords don’t match',
});

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const form = useForm<z.infer<typeof resetForm>>({ resolver: zodResolver(resetForm) });
  const mutation = useMutation({ mutationFn: (v: z.infer<typeof resetForm>) => api.post('/api/auth/reset-password', { token, password: v.password }) });
  return (
    <AuthLayout title="Choose a new password" footer={<Link className="text-accent hover:underline" to="/signin">Back to sign in</Link>}>
      {mutation.isSuccess ? (
        <Callout tone="success" icon={CircleCheck} title="Password updated">
          You’ve been signed out everywhere. <Link to="/signin" className="text-accent underline">Sign in</Link> with your new password.
        </Callout>
      ) : (
        <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
          {!token && <Callout tone="danger">This link is missing its token. Request a new reset email.</Callout>}
          {mutation.isError && <Callout tone="danger">{errorMessage(mutation.error)}</Callout>}
          <Field label="New password" error={form.formState.errors.password?.message}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" autoFocus {...form.register('password')} />}
          </Field>
          <Field label="Confirm password" error={form.formState.errors.confirm?.message}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register('confirm')} />}
          </Field>
          <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending} disabled={!token}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { data: session, isLoading: sessionLoading } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<{ email: string; role: WorkspaceRole; workspaceName: string }>(`/api/invitations/${token}`),
    enabled: !!token,
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => api.post<{ workspaceId: string; session: SessionDto }>(`/api/invitations/${token}/accept`),
    onSuccess: (res) => {
      setDone(true);
      qc.setQueryData(sessionKey, res.session);
      navigate(`/w/${res.workspaceId}/inbox`);
    },
  });
  const next = encodeURIComponent(`/invite?token=${token}`);

  if (invite.isLoading || sessionLoading) {
    return (
      <AuthLayout title="Join workspace">
        <Skeleton className="h-20" />
      </AuthLayout>
    );
  }
  if (invite.isError || !invite.data) {
    return (
      <AuthLayout title="Invitation unavailable">
        <Callout tone="danger">{errorMessage(invite.error) || 'This invitation link is invalid.'}</Callout>
      </AuthLayout>
    );
  }
  const inv = invite.data;
  return (
    <AuthLayout title={`Join ${inv.workspaceName}`} subtitle={`You’ve been invited as ${ROLE_LABELS[inv.role]} (${inv.email}).`}>
      {session ? (
        <div className="space-y-4">
          {session.user.email !== inv.email && (
            <Callout tone="warning">
              You’re signed in as {session.user.email}. This invitation is for {inv.email}.
            </Callout>
          )}
          {accept.isError && <Callout tone="danger">{errorMessage(accept.error)}</Callout>}
          <Button variant="primary" className="w-full" loading={accept.isPending || done} onClick={() => accept.mutate()}>
            Accept invitation
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button asChild variant="primary" className="flex-1">
            <Link to={`/signup?next=${next}`}>Create account</Link>
          </Button>
          <Button asChild className="flex-1">
            <Link to={`/signin?next=${next}`}>Sign in</Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
